import { Calendar, Clock, User, MapPin, Plus } from 'lucide-react';
import { useJobs } from '../../jobs/hooks/useJobs';
import { useWorkflowMetrics } from '../hooks/useWorkflowMetrics';
import { useNavigate } from 'react-router-dom';
import { isToday } from 'date-fns';
import { DateUtils, NumberUtils, type JobWithRelations } from '@cleanpro/common-ui';

export default function TodaysAppointments() {
  const { jobs, loading } = useJobs();
  const { timeBasedMetrics } = useWorkflowMetrics();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 text-center">
        Loading today's appointments...
      </div>
    );
  }

  // Get today's jobs
  const todaysJobs = (jobs as JobWithRelations[]).filter(job => {
    const jobDate = new Date(job.date_time);
    return isToday(jobDate);
  });

  // Use enhanced metrics for appointment stats
  const totalAppointments = timeBasedMetrics.today.appointmentsScheduled;
  const activeAppointments = todaysJobs.filter(job => 
    ['confirmed', 'in-progress', 'en-route'].includes(job.status)
  ).length;
  const completedAppointments = timeBasedMetrics.today.appointmentsCompleted;
  const overdueAppointments = todaysJobs.filter(job => {
    const now = new Date();
    const [hours, minutes] = job.date_time.split(':').map(Number);
    const jobTime = new Date();
    jobTime.setHours(hours, minutes, 0, 0);
    return now > jobTime && !['completed', 'cancelled'].includes(job.status);
  }).length;
  const remainingAppointments = totalAppointments - completedAppointments;

  const handleScheduleJob = () => {
    navigate('/jobs/new');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Today's appointments</h2>
        <button 
          onClick={handleScheduleJob}
          className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Schedule a Job</span>
        </button>
      </div>
      
      {/* Stats Header */}
      <div className="flex space-x-8 mb-4 text-sm border-b border-gray-200 pb-4">
        <div className="text-center">
          <div className="font-semibold text-gray-900">{totalAppointments}</div>
          <div className="text-gray-600">Total</div>
        </div>
        <div className="text-center">
          <div className="font-semibold text-blue-600">{activeAppointments}</div>
          <div className="text-gray-600">Active</div>
        </div>
        <div className="text-center">
          <div className="font-semibold text-green-600">{completedAppointments}</div>
          <div className="text-gray-600">Completed</div>
        </div>
        <div className="text-center">
          <div className="font-semibold text-red-600">{overdueAppointments}</div>
          <div className="text-gray-600">Overdue</div>
        </div>
        <div className="text-center">
          <div className="font-semibold text-orange-600">{remainingAppointments}</div>
          <div className="text-gray-600">Remaining</div>
        </div>
      </div>

      {/* Appointments List */}
      {totalAppointments === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-base font-medium mb-2">No appointments scheduled for today</p>
          <p className="text-sm">
            This is where you will get an overview of your appointments for today once they are scheduled.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {todaysJobs
            .sort((a, b) => a.date_time.localeCompare(b.date_time))
            .map((job) => (
              <div 
                key={job.id}
                onClick={() => navigate(`/jobs/${job.id}`)}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md hover:border-gray-300 cursor-pointer transition-all"
              >
                <div className="flex items-center space-x-4">
                  <div className="flex flex-col items-center text-sm">
                    <Clock className="h-4 w-4 text-gray-400 mb-1" />
                    <span className="font-medium">{DateUtils.formatTimestamptzToTime(job.date_time)}</span>
                    <span className="text-gray-500 text-xs">{DateUtils.formatDuration(job.duration)}</span>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-medium text-gray-900">{job.service?.name || 'Unknown Service'}</h3>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        job.status === 'completed' ? 'bg-green-100 text-green-800' :
                        job.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                        job.status === 'confirmed' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {job.status.replace('-', ' ')}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-1" />
                        <span>{job.customer?.name || 'Unknown Customer'}</span>
                      </div>
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        <span className="truncate max-w-xs">{job.property?.addressLine1 || 'No address'}</span>
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-500 mt-1">
                      Assigned to: {job.business?.businessName || 'Unassigned'}
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="font-semibold text-gray-900">{NumberUtils.formatCurrency(job.price)}</div>
                  {job.business?.rating && (
                    <div className="text-sm text-yellow-600">
                      ★ {job.business.rating}
                    </div>
                  )}
                </div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}
